import {
  ButtonIcon,
  ButtonSelect,
  Chip,
  Icon,
  InlineMessage,
  InputField,
  Menu,
  type MenuOption,
  Popover,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { type IntlShape } from 'react-intl';

import {
  CRITICAL_OPTIONS,
  EDIT_OWNER_TABLE_COLUMN_KEY_1,
  EDIT_OWNER_TABLE_COLUMN_KEY_2,
  EDIT_OWNER_TABLE_COLUMN_KEY_3,
  LABEL_TABLE_COLUMN_KEY_1,
  LABEL_TABLE_COLUMN_KEY_2,
  managePageMessages,
  OWNER_TABLE_COLUMN_KEY_1,
  OWNER_TABLE_COLUMN_KEY_2,
  OWNER_TABLE_COLUMN_KEY_3,
  OWNER_TABLE_COLUMN_KEY_4,
  SOURCE_CONFIG,
  TOKENS,
} from '../../constants';
import {
  capitalizeFirstLetter,
  getCriticalityProps,
  getCriticalMessage,
  getIsCritical,
  getOwnerIcon,
  getOwnerIconTooltip,
  getOwnerRankOptions,
  getOwnerRoleOptions,
} from '../../utils/utilManagepage';
import {
  type AgentOwner,
  type CriticalityLevel,
  RowDash,
} from '../ManagePageTable';

// Overview

/**
 * Builds the criticality menu options list from {@link CRITICAL_OPTIONS},
 * translating labels via `intl` and rendering a color-coded {@link Chip} for each level.
 *
 * @param intl - react-intl `IntlShape` instance for message translation.
 * @returns Array of menu option objects with `label`, `isChecked`, and a `node` Chip.
 */
export const getCriticalMenuOptions = (intl: IntlShape) =>
  CRITICAL_OPTIONS.map((co) => ({
    ...co,
    label: intl.formatMessage(managePageMessages[co.messageLabelKey]),
    isChecked: false,
    node: (
      <Chip
        size='small'
        kind={getCriticalityProps(co.value as CriticalityLevel).kind}
        label={capitalizeFirstLetter(co.value)}
        color={getCriticalityProps(co.value as CriticalityLevel).color}
        isInteractionDisabled
      />
    ),
  }));

// Labels

/**
 * Transforms a labels record into read-only table row data for the Labels tab.
 *
 * @param labels - Key-value record of agent labels.
 * @returns Array of row objects keyed by column constants, each containing formatted Typography nodes.
 */
export function getLabelRowData(labels: Record<string, string>) {
  return Object.entries(labels).map(([key, value]) => ({
    [LABEL_TABLE_COLUMN_KEY_1]: (
      <Typography kind='h4' color={TOKENS.CF_NEU_BLD} isNoWrap lineLimit={1}>
        {key}
      </Typography>
    ),
    [LABEL_TABLE_COLUMN_KEY_2]: (
      <Typography kind='body2' color={TOKENS.CF_SEC_BLD} isNoWrap lineLimit={1}>
        {value}
      </Typography>
    ),
  }));
}

/**
 * Transforms a labels record into editable table row data with inline value inputs and delete buttons.
 *
 * @param labels - Key-value record of agent labels.
 * @param setCurrentLabels - State setter to update the labels record on edit or delete.
 * @returns Array of row objects with a read-only key column and an editable value column.
 */
export function getEditLabelRowData(
  labels: Record<string, string>,
  setCurrentLabels: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >,
  intl: IntlShape,
  isSaveBtnClicked: boolean
) {
  return Object.entries(labels).map(([key, value]) => ({
    [LABEL_TABLE_COLUMN_KEY_1]: (
      <Typography kind='h4' color={TOKENS.CF_NEU_BLD} isNoWrap lineLimit={1}>
        {key}
      </Typography>
    ),
    [LABEL_TABLE_COLUMN_KEY_2]: (
      <div className='SidePanelLabelsTab-tableNewRowValueWrapper'>
        <InputField
          name='basic'
          placeholder={intl.formatMessage({
            id: 'ES5vsI',
            defaultMessage: 'Value',
            description: 'Value',
          })}
          value={value}
          setValue={(value: string) => {
            setCurrentLabels((prev) => ({
              ...prev,
              [key]: value,
            }));
          }}
          isCritical={Boolean(isSaveBtnClicked && getIsCritical(value))}
          CriticalHelperText={
            <InlineMessage
              text={getCriticalMessage(value, intl)}
              colorTheme='critical'
              size='small'
              leftIcon={<Icon kind='AlertCriticalFilled' />}
            />
          }
        />
        <ButtonIcon
          size='large'
          kind='ghost'
          icon={<Icon kind='Delete' size='medium' />}
          ariaLabel={intl.formatMessage({
            id: '+lIo7D',
            defaultMessage: 'Delete label',
            description: 'Delete label',
          })}
          onClick={() => {
            setCurrentLabels((prev) => {
              const newLabels = { ...prev };

              delete newLabels[key];

              return newLabels;
            });
          }}
        />
      </div>
    ),
  }));
}

interface GetOwnerSourceChipProps {
  source?: string | null;
  intl: IntlShape;
}

// Owners
/**
 * Renders a color-coded Chip with a tooltip indicating the owner's assignment source
 * (policy, API, manual, or ingestion). Returns a dash for unrecognised sources.
 */
function GetOwnerSourceChip({ source, intl }: GetOwnerSourceChipProps) {
  if (!source) {
    return <RowDash />;
  }

  const normalizedSource = source.toLowerCase();

  const config = SOURCE_CONFIG.find(({ match }) => match(normalizedSource));

  if (!config) {
    return <RowDash />;
  }

  return (
    <Tooltip
      trigger={
        <Chip
          kind='subtleFilled'
          color={config.color}
          size='small'
          label={source}
          isInteractionDisabled
        />
      }
      text={intl.formatMessage(managePageMessages[config.messageLabelKey])}
      placement='top'
    />
  );
}

/**
 * Renders a color-coded Chip with a tooltip describing the owner's role
 * (Business or Technical). Returns a dash for unrecognised roles.
 */
interface GetOwnerRoleChipProps {
  role: string;
  intl: IntlShape;
}

function GetOwnerRoleChip({ role, intl }: GetOwnerRoleChipProps) {
  if (role.toLowerCase().includes('business')) {
    return (
      <Tooltip
        trigger={
          <Chip
            kind='subtleFilled'
            color='brandPrimary'
            size='small'
            label={role}
            isInteractionDisabled
          />
        }
        text={intl.formatMessage({
          id: 'nuDKuG',
          defaultMessage:
            "Accountable for the agent's overall purpose, risk, and business alignment. Final decision maker for ownership and lifecycle changes.",
          description:
            "Accountable for the agent's overall purpose, risk, and business alignment. Final decision maker for ownership and lifecycle changes.",
        })}
        placement='top'
      />
    );
  }

  if (role.toLowerCase().includes('technical')) {
    return (
      <Tooltip
        trigger={
          <Chip
            kind='subtleFilled'
            color='neutral'
            size='small'
            label={role}
            isInteractionDisabled
          />
        }
        text={intl.formatMessage({
          id: '5dBdL+',
          defaultMessage:
            'Responsible for technical setup, integrations, configuration, and ongoing maintenance of the agent. Handles operational and implementation details.',
          description:
            'Responsible for technical setup, integrations, configuration, and ongoing maintenance of the agent. Handles operational and implementation details.',
        })}
        placement='top'
      />
    );
  }

  return <RowDash />;
}

/**
 * Builds read-only table row data for the Owners tab, including name/type popover,
 * source chip, role chip, and rank columns.
 *
 * @param owners - Array of {@link AgentOwner} objects.
 * @param userPopover - Boolean array tracking popover open state per row.
 * @param setUserPopover - State setter to toggle individual popovers.
 * @returns Array of row objects keyed by owner column constants.
 */
export function getOwnerRowData(
  owners: AgentOwner[],
  userPopover: boolean[],
  setUserPopover: React.Dispatch<React.SetStateAction<boolean[]>>,
  intl: IntlShape
) {
  return owners.map((o, i) => ({
    [OWNER_TABLE_COLUMN_KEY_1]: (
      <div className='OwnerRowData-nameWrapper'>
        <Tooltip
          trigger={<Icon kind={getOwnerIcon(o.type)} size='medium' />}
          text={getOwnerIconTooltip(o.type, intl)}
          placement='right'
        />
        <Popover
          classNames={{
            wrapperClassName: 'OwnerRowData-popOverWrapper',
          }}
          trigger={
            <div className='OwnerRowData-nameDetails'>
              <Typography
                kind='h3'
                color={TOKENS.CF_SEC_BLD}
                isNoWrap
                lineLimit={1}
                tooltipConfigs={{ isPortalEnabled: true }}>
                {o.name}
              </Typography>
              {o.email ? (
                <Typography kind='body2' color={TOKENS.CF_NEU_STL}>
                  {o.email}
                </Typography>
              ) : null}
            </div>
          }
          popoverContent={
            <div className='OwnerRowData-namePopoverWrapper'>
              <div className='OwnerRowData-popOverTitle'>
                <Icon kind={getOwnerIcon(o.type)} size='medium' />
                <Typography
                  kind='h3'
                  color={TOKENS.CF_SEC_BLD}
                  isNoWrap
                  lineLimit={1}
                  tooltipConfigs={{ isPortalEnabled: true }}>
                  {o.name}
                </Typography>
              </div>
              <div className='OwnerRowData-namePopoverListItem'>
                <Typography
                  kind='body3'
                  color={TOKENS.CF_NEU_STL}
                  isNoWrap
                  lineLimit={1}
                  tooltipConfigs={{ isPortalEnabled: true }}>
                  {o.name}
                </Typography>
                <Typography
                  kind='body2'
                  color={TOKENS.CF_SEC_BLD}
                  isNoWrap
                  lineLimit={1}
                  tooltipConfigs={{ isPortalEnabled: true }}>
                  {o.email}
                </Typography>
              </div>
            </div>
          }
          isOpen={userPopover[i]}
          setIsOpen={(state: boolean) => {
            setUserPopover((prev) =>
              prev.map((p, _i) => (_i === i ? state : p))
            );
          }}
          isOpenOnHover
          placement='right'
        />
      </div>
    ),
    [OWNER_TABLE_COLUMN_KEY_2]: (
      <div className='OwnerRowData-sourceWrapper'>
        <GetOwnerSourceChip source={o.source} intl={intl} />
      </div>
    ),
    [OWNER_TABLE_COLUMN_KEY_3]: (
      <div className='OwnerRowData-roleWrapper'>
        <GetOwnerRoleChip role={o.role} intl={intl} />
      </div>
    ),
    [OWNER_TABLE_COLUMN_KEY_4]: (
      <div className='OwnerRowData-rankWrapper'>
        <Typography
          kind='body2'
          color={TOKENS.CF_SEC_BLD}
          isNoWrap
          lineLimit={1}>
          {o.rank}
        </Typography>
      </div>
    ),
  }));
}

/**
 * Builds editable table row data for the Owners tab with role/rank dropdowns
 * and a remove button per row.
 *
 * @param owners - Array of {@link AgentOwner} objects.
 * @param setCurrentOwners - State setter to update the owners array on role/rank change or removal.
 * @param intl - react-intl `IntlShape` instance for translating role and rank option labels.
 * @returns Array of row objects keyed by edit-owner column constants.
 */
export function getEditOwnerRowData(
  owners: AgentOwner[],
  setCurrentOwners: React.Dispatch<React.SetStateAction<AgentOwner[]>>,
  intl: IntlShape
) {
  return owners.map((o, idx) => ({
    [EDIT_OWNER_TABLE_COLUMN_KEY_1]: (
      <div className='OwnerRowData-nameWrapper'>
        <Icon kind={getOwnerIcon(o.type)} size='medium' />
        <div className='OwnerRowData-nameDetails'>
          <Typography
            kind='h3'
            color={TOKENS.CF_SEC_BLD}
            isNoWrap
            lineLimit={1}
            tooltipConfigs={{ isPortalEnabled: true }}>
            {o.name}
          </Typography>
          {o.email ? (
            <Typography kind='body2' color={TOKENS.CF_NEU_STL}>
              {o.email}
            </Typography>
          ) : null}
        </div>
      </div>
    ),
    [EDIT_OWNER_TABLE_COLUMN_KEY_2]: (
      <div className='OwnerRowData-roleWrapper--isEditMode'>
        <Menu
          size='large'
          options={getOwnerRoleOptions(intl).map((_o) => ({
            ..._o,
            isChecked: o.role === _o.value,
          }))}
          onChange={(selectedOption: MenuOption) => {
            setCurrentOwners((prev) =>
              prev.map((row, i) =>
                i === idx ? { ...row, role: selectedOption.value } : row
              )
            );
          }}
          trigger={
            <ButtonSelect
              kind='rectangle'
              label={o.role}
              size='large'
              classNames={{
                contentContainerClassName: 'OwnerRowData-roleSelectContent',
              }}
            />
          }
          classNames={{
            triggerClassName: 'OwnerRowData-roleTrigger',
            dropdownClassName: 'OwnerRowData-roleDropdown',
          }}
        />
      </div>
    ),
    [EDIT_OWNER_TABLE_COLUMN_KEY_3]: (
      <div className='OwnerRowData-rankWrapper--isEditMode'>
        <Menu
          size='large'
          options={getOwnerRankOptions(intl).map((_o) => ({
            ..._o,
            isChecked: Number(o.rank) === Number(_o.value),
          }))}
          onChange={(selectedOption: MenuOption) => {
            setCurrentOwners((prev) =>
              prev.map((row, i) =>
                i === idx ? { ...row, rank: Number(selectedOption.value) } : row
              )
            );
          }}
          trigger={
            <ButtonSelect
              kind='rectangle'
              label={String(o.rank)}
              size='large'
              classNames={{
                contentContainerClassName: 'OwnerRowData-rankSelectContent',
              }}
            />
          }
          classNames={{
            dropdownClassName: 'OwnerRowData-rankDropdown',
          }}
        />
        <Tooltip
          trigger={
            <ButtonIcon
              kind='outlinedSecondary'
              icon={<Icon kind='Subtract' size='xSmall' />}
              ariaLabel={intl.formatMessage({
                id: 'sa1shP',
                defaultMessage: 'Remove',
                description: 'Remove',
              })}
              onClick={() => {
                setCurrentOwners((prev) => prev.filter((_, i) => i !== idx));
              }}
            />
          }
          text={intl.formatMessage({
            id: 'sa1shP',
            defaultMessage: 'Remove',
            description: 'Remove',
          })}
          placement='left'
        />
      </div>
    ),
  }));
}

/**
 * Builds a single read-only row for the platform-injected (ingestion) owner
 * in the edit modal's Assigned table. Uses the same column keys as
 * {@link getEditOwnerRowData} but renders static text instead of dropdowns,
 * and omits the remove button.
 */
export function getIngestionOwnerEditRow(owner: AgentOwner) {
  return {
    [EDIT_OWNER_TABLE_COLUMN_KEY_1]: (
      <div className='OwnerRowData-nameWrapper'>
        <Icon kind={getOwnerIcon(owner.type)} size='medium' />
        <div className='OwnerRowData-nameDetails'>
          <Typography
            kind='h3'
            color={TOKENS.CF_SEC_BLD}
            isNoWrap
            lineLimit={1}
            tooltipConfigs={{ isPortalEnabled: true }}>
            {owner.name}
          </Typography>
          {owner.email ? (
            <Typography kind='body2' color={TOKENS.CF_NEU_STL}>
              {owner.email}
            </Typography>
          ) : null}
        </div>
      </div>
    ),
    [EDIT_OWNER_TABLE_COLUMN_KEY_2]: (
      <div className='OwnerRowData-roleWrapper'>
        <Typography
          kind='body2'
          color={TOKENS.CF_SEC_BLD}
          isNoWrap
          lineLimit={1}>
          {owner.role}
        </Typography>
      </div>
    ),
    [EDIT_OWNER_TABLE_COLUMN_KEY_3]: (
      <div className='OwnerRowData-rankWrapper'>
        <Typography
          kind='body2'
          color={TOKENS.CF_SEC_BLD}
          isNoWrap
          lineLimit={1}>
          {owner.rank}
        </Typography>
      </div>
    ),
  };
}

/**
 * Renders a selectable identity row with name, type icon, and an add/remove toggle button.
 * Used in the owner-edit identity picker list.
 *
 * @param props.name - Display name of the identity.
 * @param props.isActive - Whether the identity is currently selected (controls add/remove icon).
 * @param props.isDisabled - When true, disables interaction and applies disabled styling.
 * @param props.type - Identity type string (e.g. "User", "Group") used for the icon.
 * @param props.handleClick - Callback invoked on click or Enter key press.
 */
interface RenderIdentityProps {
  isActive: boolean;
  name: string;
  type: string | null;
  isDisabled?: boolean;
  handleClick: () => void;
  intl: IntlShape;
}

export function RenderIdentity({
  name,
  isActive,
  isDisabled,
  type,
  handleClick,
  intl,
}: RenderIdentityProps) {
  return (
    <button
      type='button'
      aria-disabled='false'
      tabIndex={isDisabled ? -1 : 0}
      onClick={() => {
        if (!isDisabled) {
          handleClick();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.detail === 1 && !isDisabled) {
          handleClick();
        }
      }}
      className={cx(
        'RenderIdentity-listRowWrapper',
        isDisabled && 'RenderIdentity-listRowWrapper--isDisabled'
      )}>
      <div className='RenderIdentity-contentWrapper'>
        <Icon kind={getOwnerIcon(type)} size='medium' />
        <div className='RenderIdentity-content'>
          <Typography
            kind='h3'
            color={TOKENS.CF_SEC_BLD}
            isNoWrap
            lineLimit={1}
            tooltipConfigs={{ isPortalEnabled: true }}>
            {name}
          </Typography>
          <Typography
            kind='body2'
            color={TOKENS.CF_NEU_STL}
            isNoWrap
            lineLimit={1}>
            {type}
          </Typography>
        </div>
      </div>
      <Tooltip
        isDisabled={isDisabled}
        trigger={
          <ButtonIcon
            size='large'
            kind='outlinedSecondary'
            isSelected={isActive}
            icon={<Icon kind={isActive ? 'Subtract' : 'Add'} size='xSmall' />}
            isDisabled={isDisabled}
            ariaLabel={
              isActive
                ? intl.formatMessage({
                    id: 'sa1shP',
                    defaultMessage: 'Remove',
                    description: 'Remove',
                  })
                : intl.formatMessage({
                    id: 'F9dR1Q',
                    defaultMessage: 'Add',
                    description: 'Add',
                  })
            }
            onClick={() => {}}
            tabIndex={-1}
          />
        }
        text={
          isActive
            ? intl.formatMessage({
                id: 'sa1shP',
                defaultMessage: 'Remove',
                description: 'Remove',
              })
            : intl.formatMessage({
                id: 'F9dR1Q',
                defaultMessage: 'Add',
                description: 'Add',
              })
        }
        placement='left'
      />
    </button>
  );
}
